import { z } from 'zod';
import { MoneySchema, ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';

export const TicketStatusSchema = z.enum([
  // Appointment booked for a future time — parked OUT of the live queue (no
  // `position`, excluded from ordering / wait-estimates / active counts) until
  // an operator admits it. Entry status for `bookTicket` when isAppointment.
  'scheduled',
  'inQueue',
  'inWashingProcess',
  'readyForCollection',
  'collected',
  'cancelled',
  'hold',
]);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

export const TicketSubStageSchema = z.enum(['inBay', 'drying', 'qualityCheck']).optional();
export type TicketSubStage = z.infer<typeof TicketSubStageSchema>;

export const PaymentMethodSchema = z.enum([
  'cash',
  'duitnow_qr',
  'tng',
  'grabpay',
  'card',
  'bank_transfer',
  'voucher',
  'free',
]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const TicketPaymentSchema = z.object({
  method: PaymentMethodSchema,
  amount: MoneySchema,
  receivedAt: TimestampSchema,
  recordedBy: UidSchema,
  voucherIds: z.array(z.string()).default([]),
  promotionIds: z.array(z.string()).default([]),
  notes: z.string().max(500).optional(),
});
export type TicketPayment = z.infer<typeof TicketPaymentSchema>;

export const StatusHistoryEntrySchema = z.object({
  from: TicketStatusSchema.optional(),
  to: TicketStatusSchema,
  subStage: TicketSubStageSchema,
  at: TimestampSchema,
  by: UidSchema,
});
export type StatusHistoryEntry = z.infer<typeof StatusHistoryEntrySchema>;

/**
 * A product cross-sell line captured on the ticket snapshot (Pro feature).
 * Priced SERVER-SIDE by `priceProductSelections` at book time (flat unit price
 * × quantity) and folded into `subtotal`/`total`. Parallel to the add-on
 * lines. Absent/empty on every ticket that didn't buy a product — which is
 * every ticket for a tenant that hasn't turned the feature on.
 */
export const TicketProductLineSchema = z.object({
  id: z.string(),
  name: z.string(),
  priceSen: MoneySchema.describe('Unit price in sen (server-resolved snapshot)'),
  qty: z.number().int().positive(),
  lineTotalSen: MoneySchema.describe('priceSen × qty'),
});
export type TicketProductLine = z.infer<typeof TicketProductLineSchema>;

export const TicketSnapshotSchema = z.object({
  packageId: z.string().optional(),
  packageName: z.string().optional(),
  addOnIds: z.array(z.string()).default([]),
  /**
   * Product cross-sell lines (Pro). Defaults to `[]` so pre-feature tickets
   * (and every ticket at a tenant that never enabled products) parse
   * unchanged — the money path is identical when no product is bought.
   */
  products: z.array(TicketProductLineSchema).default([]),
  vehicleId: z.string().optional(),
  vehicleType: z.string().optional(),
  plate: z.string().optional(),
  pricingVersion: z.number().int().nonnegative(),
  subtotal: MoneySchema,
  promotionsApplied: MoneySchema.default(0),
  appliedPromotionIds: z.array(z.string()).optional(),
  /**
   * Per-promotion savings — set by bookTicket from the resolver's `applied`
   * list so /pos can show one breakdown line per promo and the customer
   * ticket can credit each operator-authored promo by name. Optional for
   * back-compat with pre-Sprint-58 tickets that only stored
   * `promotionsApplied` (total).
   */
  appliedPromotions: z.array(z.object({ id: z.string(), savingsSen: MoneySchema })).optional(),
  total: MoneySchema,
});
export type TicketSnapshot = z.infer<typeof TicketSnapshotSchema>;

/**
 * Where a booking came from — the "Share & grow" attribution (feature doc
 * `docs/features/share-and-grow.feature.md`).
 *
 * The operator's Share kit hands out links carrying `?src=<channel>&c=<moment>`;
 * the customer app keeps the first one it saw this session and passes it to
 * `bookTicket`, which stores it here. `/reports` counts paid washes by
 * `source`, so an operator can see whether the WhatsApp post or the QR on the
 * counter brought people in.
 *
 * A closed enum on purpose: a free-text source is a column of typos
 * ("whatsap", "WA", "wa.me") that no report can add up.
 */
export const AttributionSourceSchema = z.enum([
  'whatsapp',
  'facebook',
  'instagram',
  'tiktok',
  'x',
  'telegram',
  'google',
  'qr',
  'link',
  'other',
]);
export type AttributionSource = z.infer<typeof AttributionSourceSchema>;

/** Slug-shaped campaign id (a Share-kit moment id such as `promo-rainy-day`). */
export const ATTRIBUTION_CAMPAIGN_MAX = 64;
export const AttributionCampaignSchema = z
  .string()
  .min(1)
  .max(ATTRIBUTION_CAMPAIGN_MAX)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'lowercase letters, digits, - and _ only');

export const TicketAttributionSchema = z.object({
  source: AttributionSourceSchema,
  campaign: AttributionCampaignSchema.optional(),
});
export type TicketAttribution = z.infer<typeof TicketAttributionSchema>;

export const TicketSchema = z.object({
  id: ShortIdSchema,
  number: z.string().describe('Human-friendly ticket # like A042'),
  status: TicketStatusSchema,
  subStage: TicketSubStageSchema,
  position: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Queue position when status is inQueue'),
  bayId: z.string().optional(),
  customerUid: UidSchema,
  customerName: z.string().optional(),
  customerPhoneMasked: z.string().optional(),
  snapshot: TicketSnapshotSchema,
  payment: TicketPaymentSchema.optional(),
  statusHistory: z.array(StatusHistoryEntrySchema).default([]),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  isAppointment: z.boolean().default(false),
  scheduledFor: TimestampSchema.optional(),

  /**
   * Booking deposit (SPEC D10) — declared here or it is invisible.
   *
   * `bookTicket` writes `requiredPrepaymentSen` and the toyyibPay webhook
   * raises `prepaidSen` as the charge clears. Both were UNDECLARED, and
   * `z.object` strips unknown keys: every client reading a ticket through this
   * schema got the deposit silently removed. The server reads raw documents so
   * cancellation still refunded correctly, but the POS could not see that
   * anything had been paid — and asked the cashier to collect the full price
   * from a customer who had already paid part of it.
   *
   * A field the server writes and the schema omits is not a small oversight
   * here; it is the difference between a customer paying RM 25 and RM 35.
   */
  requiredPrepaymentSen: MoneySchema.optional().describe(
    'Sen the operator requires up front to hold this appointment. Absent when booking is free.',
  ),
  prepaidSen: MoneySchema.optional().describe(
    'Sen actually received so far. Raised by the payment webhook, never by the client.',
  ),
  prepaidAt: TimestampSchema.optional(),
  /** Sen returned to the customer as store credit when this booking was cancelled. */
  creditIssuedSen: MoneySchema.optional(),
  /**
   * Which shared link brought this booking in. Optional so every ticket written
   * before Share & grow — and every walk-in with no link — still parses.
   * Written only by `bookTicket`, never by a client.
   */
  attribution: TicketAttributionSchema.optional(),
});
export type Ticket = z.infer<typeof TicketSchema>;
